'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'

export default function PlanosPage() {
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  return (
    <main className="flex flex-col items-center px-4 py-8 min-h-screen bg-gray-50">

      {/* Trial CTA */}
      <div className="w-full max-w-md mb-6">
        <Link
          href="/app/trial"
          className="group flex items-center justify-between w-full bg-gray-900 text-white rounded-2xl px-5 py-4 hover:bg-gray-800 transition-all hover:shadow-lg"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-sm">Experimentar grátis por 24h</p>
              <p className="text-white/60 text-xs">3 moedas inclusos. Sem cartão. Ativa via SMS.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">GRÁTIS</span>
            <svg className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 font-medium">ou assine um plano</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Arquitetura de Negócios Digitais
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Acesse o sistema completo de construção de negócios digitais
          </p>
        </div>

        {/* Card do plano */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Acesso Completo</h2>
            <div className="text-right">
              <span className="text-3xl font-bold text-gray-900">R$ 19,90</span>
              <span className="text-sm text-gray-400"> / 7 dias</span>
            </div>
          </div>
          <ul className="space-y-2 text-sm text-gray-600">
            {[
              'Sistema completo de prompts guiados',
              'Biblioteca pessoal de outputs',
              'Acesso a toda documentação',
              'Atualizações e novos fluxos inclusos',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Button */}
        {!showCheckout && (
          <button
            onClick={() => {
              setShowCheckout(true)
              setCheckoutError('')
            }}
            className="w-full bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors cursor-pointer mb-4"
          >
            Assinar por R$ 19,90
          </button>
        )}

        {/* Checkout Transparente */}
        {showCheckout && (
          <div className="mb-4">
            {checkoutError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {checkoutError}
              </div>
            )}

            <TransparentCheckout
              channelSlug="planos"
              buyerName={buyerName}
              buyerEmail={buyerEmail}
              buyerPhone={buyerPhone}
              buyerCpf={buyerCpf}
              onSuccess={(paymentId) => {
                router.push('/app?payment=' + paymentId)
              }}
              onError={(error) => {
                setCheckoutError(error)
              }}
            />
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-4">
          Pagamento processado com segurança via Assas.
        </p>
      </div>
    </main>
  )
}
